import React, { useEffect, useMemo, useState } from "react";
import { Percent, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty } from "@/pages/shared";

const RateTable = ({ title, summ, t }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`vat-src-${title}`}>
    <p className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground bg-muted/40 flex items-center justify-between">
      <span>{title}</span><span className="tabular-nums">{summ.count} · {money(summ.incl)}</span>
    </p>
    {summ.per_rate.length === 0 ? (
      <p className="px-3 py-3 text-xs text-muted-foreground">{t("no_data")}</p>
    ) : (
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-muted-foreground">
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("btw")} %</th>
            <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("excl_vat")}</th>
            <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("btw")}</th>
            <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("incl_vat")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {summ.per_rate.map((r) => (
            <tr key={r.rate} data-testid={`vat-rate-${title}-${r.rate}`}>
              <td className="px-3 py-2 font-semibold">{r.rate}%</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(r.base)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-500">{money(r.vat)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold">{money(r.incl)}</td>
            </tr>
          ))}
          <tr className="bg-muted/30 font-bold">
            <td className="px-3 py-2">{t("total")}</td>
            <td className="px-3 py-2 text-right tabular-nums">{money(summ.base)}</td>
            <td className="px-3 py-2 text-right tabular-nums">{money(summ.vat)}</td>
            <td className="px-3 py-2 text-right tabular-nums">{money(summ.incl)}</td>
          </tr>
        </tbody>
      </table>
    )}
  </div>
);

export default function AdminVat() {
  const { t } = useLang();
  const now = new Date();
  const [mode, setMode] = useState("quarter");
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rep, setRep] = useState(null);

  const query = useMemo(() => {
    if (mode === "custom") return from && to ? `start=${from}&end=${to}` : null;
    if (mode === "quarter") {
      const sm = (quarter - 1) * 3;
      const start = `${year}-${String(sm + 1).padStart(2, "0")}-01`;
      const endDate = new Date(Date.UTC(year, sm + 3, 0));
      return `start=${start}&end=${endDate.toISOString().slice(0, 10)}`;
    }
    return `year=${year}`;
  }, [mode, year, quarter, from, to]);

  useEffect(() => {
    if (!query) { setRep(null); return; }
    api.get(`/reports/vat?${query}`).then(({ data }) => setRep(data)).catch(() => setRep(null));
  }, [query]);

  const fileBase = rep?.label ? rep.label.replace(/\s→\s/, "_").replace(/\s/g, "") : year;
  const saldoPositive = (rep?.saldo || 0) >= 0;

  const exportCsv = () => {
    if (!rep) return;
    const lines = [["bron", "btw%", "excl", "btw", "incl"].join(";")];
    const add = (name, s) => s.per_rate.forEach((r) => lines.push([name, r.rate, r.base.toFixed(2), r.vat.toFixed(2), r.incl.toFixed(2)].join(";")));
    add("losse_verkopen", rep.sources.sales);
    add("facturen", rep.sources.invoices);
    add("voorschotten", rep.sources.deposits);
    add("aankopen", rep.input);
    lines.push("");
    lines.push(["verschuldigde_btw", "", "", rep.output.vat.toFixed(2), ""].join(";"));
    lines.push(["aankoop_btw", "", "", rep.input.vat.toFixed(2), ""].join(";"));
    lines.push(["saldo", "", "", rep.saldo.toFixed(2), ""].join(";"));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `btw-${fileBase}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const exportPdf = async () => {
    if (!query) return;
    try {
      const { data } = await api.get(`/reports/vat.pdf?${query}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* genegeerd */ }
  };

  return (
    <Panel
      title={`${t("admin_vat")} · ${rep?.label || year}`}
      testid="vat-panel"
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="vat-export-csv"><Download className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="outline" size="sm" onClick={exportPdf} data-testid="vat-export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
        </div>
      }
    >
      <div className="px-5 pt-4 flex flex-wrap items-center gap-2" data-testid="vat-filters">
        <div className="inline-flex rounded-lg border border-border overflow-hidden">
          {[["year", t("per_year")], ["quarter", t("per_quarter")], ["custom", t("custom_range")]].map(([m, lbl]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`vat-mode-${m}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-muted"}`}>{lbl}</button>
          ))}
        </div>
        {mode !== "custom" && (
          <div className="inline-flex items-center gap-1">
            <button onClick={() => setYear((y) => y - 1)} className="px-2 py-1 rounded hover:bg-muted" data-testid="vat-year-prev">‹</button>
            <span className="font-display font-bold tabular-nums" data-testid="vat-year">{year}</span>
            <button onClick={() => setYear((y) => y + 1)} className="px-2 py-1 rounded hover:bg-muted" data-testid="vat-year-next">›</button>
          </div>
        )}
        {mode === "quarter" && (
          <div className="inline-flex rounded-lg border border-border overflow-hidden">
            {[1, 2, 3, 4].map((q) => (
              <button key={q} onClick={() => setQuarter(q)} data-testid={`vat-q-${q}`}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${quarter === q ? "bg-secondary text-primary" : "bg-card text-muted-foreground hover:bg-muted"}`}>Q{q}</button>
            ))}
          </div>
        )}
        {mode === "custom" && (
          <div className="inline-flex items-center gap-2">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-lg border border-border bg-background px-2 text-sm" data-testid="vat-from" />
            <span className="text-muted-foreground">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 rounded-lg border border-border bg-background px-2 text-sm" data-testid="vat-to" />
          </div>
        )}
      </div>

      {!rep ? <Empty /> : (
        <div className="p-5 space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4" data-testid="vat-card-output">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{t("vat_due")}</p>
              <p className="font-display text-2xl font-extrabold text-sky-700 tabular-nums">{money(rep.output.vat)}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4" data-testid="vat-card-input">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{t("vat_input")}</p>
              <p className="font-display text-2xl font-extrabold text-slate-600 tabular-nums">{money(rep.input.vat)}</p>
            </div>
            <div className={`rounded-xl border p-4 ${saldoPositive ? "border-rose-300 bg-rose-50 dark:bg-rose-950/30" : "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30"}`} data-testid="vat-card-saldo">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{saldoPositive ? t("vat_to_pay") : t("vat_to_reclaim")}</p>
              <p className={`font-display text-2xl font-extrabold tabular-nums ${saldoPositive ? "text-rose-700" : "text-emerald-700"}`}>{money(Math.abs(rep.saldo))}</p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <RateTable title={t("sales")} summ={rep.sources.sales} t={t} />
            <RateTable title={t("invoices")} summ={rep.sources.invoices} t={t} />
            <RateTable title={t("admin_prepayments")} summ={rep.sources.deposits} t={t} />
            <RateTable title={t("admin_purchases")} summ={rep.input} t={t} />
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="vat-note">{t("vat_deposit_note")} · {t("vat_input_note")}</p>

          {rep.quarters.length > 0 && (
            <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="vat-quarters">
              <p className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground bg-muted/40">{t("per_quarter")}</p>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("period")}</th>
                    <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("vat_due")}</th>
                    <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("vat_input")}</th>
                    <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("saldo")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rep.quarters.map((q) => (
                    <tr key={q.key} data-testid={`vat-quarter-${q.key.replace(/[^0-9Q]/g, "")}`}>
                      <td className="px-3 py-2 font-semibold">{q.key}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-sky-700">{money(q.output_vat)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{money(q.input_vat)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${q.saldo >= 0 ? "text-rose-700" : "text-emerald-700"}`}>{money(q.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}
